import { useEffect, useCallback, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface ScanQueueItem {
  id: string;
  template_id: string;
  template_name: string;
  field_data: Record<string, string>;
  created_at: string;
}

interface UseScanQueueOptions {
  onScanReceived?: (item: ScanQueueItem) => void;
  enabled?: boolean;
}

const POLL_INTERVAL = 3000;

/**
 * Polls for incoming scanned labels from the scan_queue table.
 * Uses polling instead of realtime to ensure data is only visible to the owning user via RLS.
 */
export function useScanQueue({ onScanReceived, enabled = true }: UseScanQueueOptions) {
  const { user } = useAuth();
  const [pendingScans, setPendingScans] = useState<ScanQueueItem[]>([]);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const onScanReceivedRef = useRef(onScanReceived);
  onScanReceivedRef.current = onScanReceived;

  const loadPending = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('scan_queue' as any)
      .select('id, template_id, template_name, field_data, created_at')
      .eq('user_id', user.id)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(50);
    if (data) {
      const items = data as any as ScanQueueItem[];
      // Detect new items
      for (const item of items) {
        if (!knownIdsRef.current.has(item.id)) {
          knownIdsRef.current.add(item.id);
          onScanReceivedRef.current?.(item);
          // Only toast for items that appeared after initial load
          if (knownIdsRef.current.size > items.length) {
            toast.info(`📱 Scan received: ${item.template_name}`);
          }
        }
      }
      // Seed known IDs on first load
      if (knownIdsRef.current.size === 0) {
        items.forEach(i => knownIdsRef.current.add(i.id));
      }
      setPendingScans(items);
    }
  }, [user]);

  // Initial load + polling
  useEffect(() => {
    if (!enabled || !user) return;
    knownIdsRef.current = new Set();
    loadPending();
    const interval = setInterval(loadPending, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [enabled, user, loadPending]);

  const dismissScan = useCallback(async (id: string) => {
    await supabase
      .from('scan_queue' as any)
      .update({ status: 'processed' } as any)
      .eq('id', id);
    knownIdsRef.current.delete(id);
    setPendingScans(prev => prev.filter(s => s.id !== id));
  }, []);

  const applyAndDismiss = useCallback(async (id: string) => {
    await supabase
      .from('scan_queue' as any)
      .update({ status: 'applied' } as any)
      .eq('id', id);
    knownIdsRef.current.delete(id);
    setPendingScans(prev => prev.filter(s => s.id !== id));
  }, []);

  return { pendingScans, dismissScan, applyAndDismiss };
}

export type { ScanQueueItem };
